/**
 * Level Completion Guard
 * ─────────────────────────────────────────────────────────────────────────────
 * Structural defense to prevent ANY restoration path from rehydrating a quiz
 * for a (category, level) pair that the user has already passed.
 *
 * Rule: if a (category, level) is approved in user_level_progress OR
 * quiz_attempts, every restoration source must discard its cached session and
 * redirect the user back to the dashboard.
 *
 * Restoration sources covered:
 *   - quiz-session-${category}-${level}   (localStorage, via useQuizSessionPersistence.loadQuizSession)
 *   - app-complete-state                  (localStorage + sessionStorage, via useAppStateRestoration)
 *   - mblearn.lastStudyPoint.v1.${user}   (localStorage + user_last_study_point, via useLastSession)
 *
 * This module exposes:
 *   - a synchronous local cache (`isLevelCompletedLocal`) populated on
 *     finalize-quiz success and on cross-device DB sync;
 *   - an async sync helper (`syncCompletedLevelsFromDB`) that hydrates the
 *     cache from `user_level_progress` so refreshes / new devices also work;
 *   - `purgeCompletedLevelArtifacts` that wipes every local storage trace
 *     for a finished (category, level).
 */
import { supabase } from "@/integrations/supabase/client";
import {
  clearLastStudyPoint,
  getLastStudyPoint,
} from "@/lib/last-study-point";

const COMPLETED_LEVELS_PREFIX = "mblearn.completedLevels.v1";
const COMPLETED_LEVELS_EVENT = "mblearn:completed-levels-updated";
const QUIZ_SESSION_PREFIX = "quiz-session-";

const lastSyncedAt = new Map<string, number>();
const SYNC_TTL_MS = 60 * 1000;

const storageKey = (userId: string) => `${COMPLETED_LEVELS_PREFIX}.${userId}`;

const SUPABASE_AUTH_TOKEN_KEY = "sb-zldkeibjkpjfrsxzogln-auth-token";

/**
 * Best-effort synchronous resolution of the current user id, parsed from the
 * Supabase auth token already cached in localStorage. Used by restoration
 * paths that run before any hook has resolved `useAuth()`.
 */
export function getCurrentUserIdSync(): string | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(SUPABASE_AUTH_TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (
      parsed?.user?.id ??
      parsed?.currentSession?.user?.id ??
      parsed?.[0]?.user?.id ??
      null
    );
  } catch {
    return null;
  }
}
const pairKey = (category: string, level: number) => `${category}:${level}`;

function readSet(userId: string): Set<string> {
  try {
    if (typeof window === "undefined") return new Set();
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
}

function writeSet(userId: string, set: Set<string>): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(storageKey(userId), JSON.stringify([...set]));
    window.dispatchEvent(new CustomEvent(COMPLETED_LEVELS_EVENT));
  } catch {
    /* swallow */
  }
}

/** Synchronous check against the local cache. */
export function isLevelCompletedLocal(
  userId: string | null | undefined,
  category: string | null | undefined,
  level: number | null | undefined,
): boolean {
  if (!userId || !category || typeof level !== "number") return false;
  return readSet(userId).has(pairKey(category, level));
}

/** Mark a (category, level) as completed in the local cache. */
export function markLevelCompletedLocal(
  userId: string,
  category: string,
  level: number,
): void {
  const set = readSet(userId);
  set.add(pairKey(category, level));
  writeSet(userId, set);
}

/**
 * Remove every local artifact tied to a completed (category, level):
 * - the quiz-session-* slot
 * - the app-complete-state if its sessionDetails point to this pair
 * - the last_study_point (local + remote) if it points to this pair
 */
export function purgeCompletedLevelArtifacts(
  userId: string | null | undefined,
  category: string,
  level: number,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(`${QUIZ_SESSION_PREFIX}${category}-${level}`);
  } catch {
    /* swallow */
  }

  // app-complete-state
  try {
    const raw = window.localStorage.getItem("app-complete-state");
    if (raw) {
      const parsed = JSON.parse(raw);
      const d = parsed?.sessionDetails;
      if (d && d.category === category && d.level === level) {
        window.localStorage.removeItem("app-complete-state");
        window.localStorage.removeItem("app-state-timestamp");
        try {
          window.sessionStorage.removeItem("app-complete-state_backup");
          window.sessionStorage.removeItem("app-state-timestamp_backup");
        } catch {/* swallow */}
      }
    }
  } catch {
    /* swallow */
  }

  // last_study_point
  if (userId) {
    const lsp = getLastStudyPoint(userId);
    if (lsp && lsp.category === category && lsp.level === level) {
      clearLastStudyPoint(userId, { category, level });
    }
  }
}

/**
 * Convenience helper: if completed, purge every restoration artifact and
 * return true so the caller can skip the restore.
 */
export function ensureNotCompletedOrPurge(
  userId: string | null | undefined,
  category: string,
  level: number,
): boolean {
  if (!isLevelCompletedLocal(userId, category, level)) return false;
  purgeCompletedLevelArtifacts(userId, category, level);
  return true;
}

/**
 * Hydrate the local completion cache from the database. Called on login,
 * dashboard load, and tab focus to keep cross-device state consistent.
 */
export async function syncCompletedLevelsFromDB(
  userId: string | null | undefined,
  options: { force?: boolean } = {},
): Promise<void> {
  if (!userId) return;
  const now = Date.now();
  if (!options.force) {
    const prev = lastSyncedAt.get(userId) ?? 0;
    if (now - prev < SYNC_TTL_MS) return;
  }
  lastSyncedAt.set(userId, now);

  try {
    const { data, error } = await supabase
      .from("user_level_progress")
      .select("category, level, completed")
      .eq("user_id", userId)
      .eq("completed", true);

    if (error) {
      console.warn("[completion-guard] DB sync failed", error);
      return;
    }

    const set = readSet(userId);
    let mutated = false;
    for (const row of (data ?? []) as Array<{ category: string; level: number }>) {
      const k = pairKey(row.category, row.level);
      if (!set.has(k)) {
        set.add(k);
        mutated = true;
      }
    }
    if (mutated) writeSet(userId, set);
  } catch (err) {
    console.warn("[completion-guard] DB sync error", err);
  }
}

export const COMPLETED_LEVELS_UPDATED_EVENT = COMPLETED_LEVELS_EVENT;
